import React, { useEffect, useState } from "react";
import {
  Card,
  message,
  Statistic,
  Table,
  Button,
  Select,
  Checkbox,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterFilled,
} from "@ant-design/icons";
import { Footer, LoadingIcon, Sidebar } from "../../../components";
import Dropdowns from "./Dropdowns";
import dayjs from "dayjs";
import {
  getSubplotDataDetails,
  createSubPlot,
  updateSubPlot,
  deleteSubPlot,
  getSubPlots,
  getPlot,
} from "../../../utils/api";
import { COLORS } from "../../../consts";

const { Option } = Select;

const naturalSort = (a, b, key) => {
  const aVal = a[key]?.toString() || "";
  const bVal = b[key]?.toString() || "";

  return aVal.localeCompare(bVal, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const dataArrayFormat = (data) => {
  return Array.isArray(data)
    ? data
    : typeof data === "object" && data !== null
      ? Object.keys(data).length === 0
        ? []
        : [data]
      : [];
};

const ScanningSessions = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState({});
  const [currentGreenhouse, setCurrentGreenhouse] = useState(null);
  const [greenhouseData, setGreenhouseData] = useState([]);
  // const [currentPlot, setCurrentPlot] = useState(null);
  const [plotData, setPlotData] = useState([]);
  // const [currentSubplot, setCurrentSubplot] = useState(null);
  // const [subplotData, setSubplotData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [dateData, setDateData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [metricData, setMetricData] = useState([]);

  const [scanningSessions, setScanningSessions] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  // For message API
  const [messageApi, contextHolder] = message.useMessage();

  const [plotRowsCache, setPlotRowsCache] = useState({});

  const getPlotCacheKey = (greenhouseId, plotId) => `${greenhouseId || ""}---${plotId || ""}`;

  const findPlotForCurrentGreenhouse = (plotId) =>
    plotData.find(
      (p) =>
        p.id === plotId &&
        (!p.greenhouse_id || p.greenhouse_id === currentGreenhouse?.id),
    ) || plotData.find((p) => p.id === plotId);

  // ---- State for "Create Instruction" table rows ----
  const [instructionRows, setInstructionRows] = useState([]);

  /**
   * Disable date logic as you have
   */
  const disabledDate = (cur) => {
    const curDate = cur.startOf("day");
    const today = dayjs().startOf("day");

    const allowedDates = dateData.map((dateStr) =>
      dayjs(dateStr).startOf("day"),
    );
    const isInAllowedList = allowedDates.some((allowed) =>
      allowed.isSame(curDate, "day"),
    );

    const diffFromToday = curDate.diff(today, "day");
    const isWithinNext14Days = diffFromToday >= 1 && diffFromToday <= 14;

    return !(isInAllowedList || isWithinNext14Days);
  };

  /**
   * 1) Fetch scanningSessions & instructions from getSubplotDataDetails
   */
  useEffect(() => {
    async function fetchData() {
      if (!currentDate || !currentGreenhouse) return;
      const selectedDateStr = dayjs(currentDate).format("YYYY-MM-DD");

      setLoading(true);
      try {
        const response = await getSubplotDataDetails(
          currentOrg,
          currentSite?.id,
          currentCrop?.id,
          [currentGreenhouse.id],
          null,
          null,
          selectedDateStr,
        );

        if (response?.status === 200 && response.data?.data?.length > 0) {
          const allScanningSessions = [];
          const allInstructions = [];
          response.data.data.forEach((dataItem) => {
            dataArrayFormat(dataItem?.scanning_sessions)?.forEach((session) => {
              allScanningSessions.push(session);
            });
            dataArrayFormat(dataItem?.instruction)?.forEach((instruction) => {
              allInstructions.push(instruction);
            });
          });
          setScanningSessions(allScanningSessions);
          setInstructions(allInstructions);
        } else {
          setScanningSessions([]);
          setInstructions([]);
        }
      } catch (error) {
        messageApi.error("Failed to fetch data.");
      }
      setLoading(false);
    }
    fetchData();
  }, [currentDate, currentGreenhouse]);

  /**
   * 2) If current/future date, fetch older instructions from getSubPlots
   */
  const selectedDateStr = currentDate
    ? dayjs(currentDate).format("YYYY-MM-DD")
    : null;
  const isPastDate = selectedDateStr && dateData.includes(currentDate);
  const isCurrentOrFutureDate = selectedDateStr && !isPastDate;

  useEffect(() => {
    async function fetchExistingRows() {
      if (!isCurrentOrFutureDate || !currentGreenhouse?.id || !currentDate) {
        setInstructionRows([]);
        return;
      }
      setTableLoading(true);
      try {
        const resp = await getSubPlots(
          currentOrg,
          currentSite?.id,
          currentCrop?.id,
          null,
          currentDate,
          currentGreenhouse.id,
          false,
        );

        if (resp.data.success) {
          const existing = resp.data.data || [];
          const existingRows = existing.map((sp) => ({
            key: sp.id,
            id: sp.id,
            greenhouse: sp?.greenhouse?.name || currentGreenhouse?.name,
            plot: sp?.plot?.id || null,
            row: sp.name || "",
            sides: Array.isArray(sp.subplot_data)
              ? sp.subplot_data.map((d) => d.side)
              : [],
            isNew: false,
          }));

          setInstructionRows([
            ...existingRows,
            {
              key: `new-${Date.now()}`,
              greenhouse: currentGreenhouse?.name || "",
              plot: null,
              row: "",
              sides: [],
              isNew: true,
            },
          ]);
        } else {
          setInstructionRows([
            {
              key: `new-0`,
              greenhouse: currentGreenhouse?.name || "",
              plot: null,
              row: "",
              sides: [],
              isNew: true,
            },
          ]);
        }
      } catch (err) {
        messageApi.error(
          "Failed to fetch existing instructions for greenhouse.",
        );
        setInstructionRows([
          {
            key: `new-0`,
            greenhouse: currentGreenhouse?.name || "",
            plot: null,
            row: "",
            sides: [],
            isNew: true,
          },
        ]);
      } finally {
        setTableLoading(false);
      }
    }
    fetchExistingRows();
  }, [isCurrentOrFutureDate, currentGreenhouse, currentDate]);

  /**
   * Build scanning vs instructions table for Past Date
   */
  const instructionMap = new Map();
  (instructions || []).forEach((inst) => {
    const key = `${inst.growing_block}-${inst.bay}-${inst.row}-${inst.side}`;
    instructionMap.set(key, inst);
  });

  const scanningMap = new Map();
  Object.values(scanningSessions || []).forEach((scan) => {
    const key = `${scan.growing_block}-${scan.bay}-${scan.row}-${scan.side}`;
    scanningMap.set(key, scan);
  });

  // Fulfilled tasks (in both)
  const fulfilledTasks = Array.from(instructionMap.keys()).filter((key) =>
    scanningMap.has(key),
  );
  // Not instructed
  const extraScannedTasks = Array.from(scanningMap.keys()).filter(
    (key) => !instructionMap.has(key),
  );
  // Missed
  const missedTasks = Array.from(instructionMap.keys()).filter(
    (key) => !scanningMap.has(key),
  );

  /**
   * Past Date columns
   */
  const columns = [
    {
      title: "Growing Block",
      dataIndex: "growing_block",
      key: "growing_block",
      sorter: (a, b) => naturalSort(a, b, "growing_block"),
      render: (value) => {
        if (!value) return "-";
        return value
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
    },
    {
      title: "Bay",
      dataIndex: "bay",
      key: "bay",
      sorter: (a, b) => naturalSort(a, b, "bay"),
    },
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
      sorter: (a, b) => naturalSort(a, b, "row"),
    },
    {
      title: "Side",
      dataIndex: "side",
      key: "side",
    },
    {
      title: "Fulfilled",
      dataIndex: "fulfilled",
      key: "fulfilled",
      render: (value) => {
        const iconMap = {
          fulfilled: (
            <Tooltip
              title="Task scanned as instructed."
              color={COLORS.primaryBlue}
            >
              <CheckCircleFilled
                style={{ fontSize: "16px", color: COLORS.successGreen }}
              />
            </Tooltip>
          ),
          extra: (
            <Tooltip
              title="Task scanned without instruction."
              color={COLORS.primaryBlue}
            >
              <CloseCircleFilled
                style={{ fontSize: "16px", color: COLORS.dangerRed }}
              />
            </Tooltip>
          ),
          missed: (
            <Tooltip
              title="Task instructed but not scanned."
              color={COLORS.primaryBlue}
            >
              <ExclamationCircleFilled
                style={{ fontSize: "16px", color: COLORS.alertYellow }}
              />
            </Tooltip>
          ),
        };
        return iconMap[value] || null;
      },
      filters: [
        { text: "Fulfilled", value: "fulfilled" },
        { text: "Extra", value: "extra" },
        { text: "Missed", value: "missed" },
      ],
      onFilter: (value, record) => record.fulfilled === value,
      filterMultiple: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Checkbox.Group
            options={[
              {
                label: "Completed as Instructed",
                value: "fulfilled",
              },
              {
                label: "Executed Without Instruction",
                value: "extra",
              },
              {
                label: "Instructed but Not Executed",
                value: "missed",
              },
            ]}
            value={selectedKeys}
            onChange={(values) => {
              setSelectedKeys(values);
            }}
            style={{ display: "flex", flexDirection: "column" }}
          />
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              OK
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 60 }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <FilterFilled
          style={{
            color: filtered ? COLORS.primaryBlue : undefined,
            fontSize: "16px",
          }}
        />
      ),
    },
    {
      title: "Task Type",
      key: "taskType",
      render: (_, record) => {
        if (record.fulfilled === "missed") return "Instructed";
        return "Scanned";
      },
    },
  ];

  // Build tableData for Past Date
  const tableData = [
    ...fulfilledTasks.map((key) => ({
      ...instructionMap.get(key),
      fulfilled: "fulfilled",
    })),
    ...extraScannedTasks.map((key) => ({
      ...scanningMap.get(key),
      fulfilled: "extra",
    })),
    ...missedTasks.map((key) => ({
      ...instructionMap.get(key),
      fulfilled: "missed",
    })),
  ].sort((a, b) => {
    const blockCompare = naturalSort(a, b, "growing_block");
    if (blockCompare !== 0) return blockCompare;

    const bayCompare = naturalSort(a, b, "bay");
    if (bayCompare !== 0) return bayCompare;

    return naturalSort(a, b, "row");
  });

  // For the statistic color
  const getFulfilledColor = () => {
    if (fulfilledTasks?.length === instructions?.length)
      return COLORS.successGreen;
    if (fulfilledTasks?.length > 0) return COLORS.alertYellow;
    return COLORS.dangerRed;
  };

  const getScannedFulfilledColor = () => {
    if (scanningSessions?.length === instructions?.length)
      return COLORS.successGreen;
    if (
      scanningSessions?.length > 0 &&
      scanningSessions.length < instructions?.length
    )
      return COLORS.dangerRed;
    return COLORS.black;
  };

  /**
   * ---------------- CREATE / UPDATE / DELETE logic for "InstructionRows" ----------------
   */
  const handleCreateInstruction = async (record) => {
    if (!record.plot || !record.row || !record.sides?.length) {
      messageApi.error("Please select Bay, fill Row, and select Side(s).");
      return;
    }
    setTableLoading(true);

    const selectedPlot = findPlotForCurrentGreenhouse(record.plot);
    const payload = {
      name: record.row,
      organization: currentOrg,
      site_id: currentSite?.id,
      crop_id: currentCrop?.id,
      greenhouse_id: currentGreenhouse?.id,
      plot_id: record.plot,
      date: selectedDateStr,
      subplot_data: record.sides.map((side) => ({
        growing_block: currentGreenhouse?.name,
        bay: selectedPlot?.name,
        row: record.row,
        side,
      })),
    };

    try {
      const res = await createSubPlot(payload);
      if (res.data.success) {
        messageApi.success("Instruction created successfully.");
        setInstructions((prev) => [...(prev || []), payload]);

        setInstructionRows((prev) =>
          prev
            .map((r) => {
              if (r.key === record.key) {
                return {
                  ...r,
                  isNew: false,
                  id: res.data.id,
                };
              }
              return r;
            })
            .concat([
              {
                key: `new-${Date.now()}`,
                greenhouse: currentGreenhouse?.name || "",
                plot: null,
                row: "",
                sides: [],
                isNew: true,
              },
            ]),
        );
      } else {
        messageApi.error("Failed to create instruction.");
      }
    } catch (err) {
      messageApi.error("Failed to create instruction.");
    } finally {
      setTableLoading(false);
    }
  };

  const handleEditInstruction = async (record) => {
    if (!record.id) return;
    if (!record.plot || !record.row || !record.sides?.length) {
      messageApi.error("Please select Bay, fill Row, and select Side(s).");
      return;
    }
    setTableLoading(true);

    const selectedPlot = findPlotForCurrentGreenhouse(record.plot);
    const payload = {
      id: record.id,
      name: record.row,
      organization: currentOrg,
      site_id: currentSite?.id,
      crop_id: currentCrop?.id,
      greenhouse_id: currentGreenhouse?.id,
      plot_id: record.plot,
      date: selectedDateStr,
      subplot_data: record.sides.map((side) => ({
        growing_block: currentGreenhouse?.name,
        bay: selectedPlot?.name,
        row: record.row,
        side,
      })),
    };

    try {
      const res = await updateSubPlot(record.id, payload);
      if (res.data.success) {
        messageApi.success("Instruction updated successfully.");
      } else {
        messageApi.error("Failed to update instruction.");
      }
    } catch (err) {
      messageApi.error("Failed to update instruction.");
    } finally {
      setTableLoading(false);
    }
  };

  const handleDeleteInstruction = async (record) => {
    if (!record.id) {
      // brand-new row not saved
      setInstructionRows((prev) => prev.filter((r) => r.key !== record.key));
      return;
    }
    setTableLoading(true);
    try {
      const res = await deleteSubPlot(record.id);
      if (res.data.success) {
        messageApi.success("Instruction deleted successfully.");
        setInstructions((prev) =>
          (prev || []).filter((inst) => inst.id !== record.id),
        );
        setInstructionRows((prev) => prev.filter((r) => r.id !== record.id));
      } else {
        messageApi.error("Failed to delete instruction.");
      }
    } catch (err) {
      messageApi.error("Failed to delete instruction.");
    } finally {
      setTableLoading(false);
    }
  };

  /**
   * For the Row column, we fetch from getPlot(plotId) => read metadata.ROWS => array of strings
   * If not found or empty => show an empty dropdown & info message.
   */
  const fetchPlotRows = async (plotId, greenhouseId = currentGreenhouse?.id) => {
    const cacheKey = getPlotCacheKey(greenhouseId, plotId);
    // If we already have it in cache, just return
    if (plotRowsCache[cacheKey]) {
      return plotRowsCache[cacheKey];
    }
    try {
      const res = await getPlot(plotId);
      const rowsArray = res?.data?.data?.metadata?.ROWS;
      if (Array.isArray(rowsArray) && rowsArray.length > 0) {
        setPlotRowsCache((prev) => ({ ...prev, [cacheKey]: rowsArray }));
        return rowsArray;
      } else {
        // No ROWS data => store empty
        setPlotRowsCache((prev) => ({ ...prev, [cacheKey]: [] }));
        messageApi.info("Please upload ROW to plot metadata.");
        return [];
      }
    } catch (err) {
      messageApi.info("Please upload ROW to plot metadata.");
      setPlotRowsCache((prev) => ({ ...prev, [cacheKey]: [] }));
      return [];
    }
  };

  const numericFamilyCompare = (aStr, bStr) => {
    const parse = (s) => {
      const str = (s ?? "").toString();
      const match = str.match(/^(\d+)/);
      const num = match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
      const rest = str.slice(match ? match[1].length : 0).toLowerCase();

      const rank =
        rest === ""
          ? 0
          : rest.includes("new")
            ? 1
            : rest.includes("old")
              ? 2
              : 3;

      return { num, rank, str };
    };

    const A = parse(aStr);
    const B = parse(bStr);

    if (A.num !== B.num) return A.num - B.num; // numeric part
    if (A.rank !== B.rank) return A.rank - B.rank; // NEW before OLD
    return A.str.localeCompare(B.str, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  /**
   * Columns for current/future date creation table
   */
  const creationColumns = [
    {
      title: "Growing Block",
      dataIndex: "greenhouse",
      render: () => <span>{currentGreenhouse?.name || "N/A"}</span>,
    },
    {
      title: "Bay",
      dataIndex: "plot",
      render: (_, record) => (
        <Select
          placeholder="Select Bay"
          style={{ width: "100%" }}
          value={record.plot}
          onChange={async (val) => {
            // When user picks a new plot => we reset row to ""
            setInstructionRows((prev) =>
              prev.map((row) =>
                row.key === record.key ? { ...row, plot: val, row: "" } : row,
              ),
            );
            // Also fetch the ROWS in the background if not cached
            await fetchPlotRows(val, currentGreenhouse?.id);
          }}
        >
          {[...plotData]
            .sort((p1, p2) => numericFamilyCompare(p1.name, p2.name))
            .map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      title: "Row",
      dataIndex: "row",
      render: (_, record) => {
        const rowOptions =
          plotRowsCache[getPlotCacheKey(currentGreenhouse?.id, record.plot)] ||
          [];
        // If user hasn’t selected a plot or we haven’t fetched yet => rowOptions is empty
        return (
          <Select
            placeholder="Select Row"
            style={{ width: "100%" }}
            value={record.row || undefined}
            onChange={(val) =>
              setInstructionRows((prev) =>
                prev.map((r) =>
                  r.key === record.key ? { ...r, row: val } : r,
                ),
              )
            }
          >
            {[...rowOptions].sort(numericFamilyCompare).map((r) => (
              <Option key={r} value={r}>
                {r}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Side",
      dataIndex: "sides",
      render: (_, record) => (
        <Select
          mode="multiple"
          placeholder="Select Side(s)"
          style={{ width: "100%" }}
          value={record.sides}
          onChange={(val) =>
            setInstructionRows((prev) =>
              prev.map((row) =>
                row.key === record.key ? { ...row, sides: val } : row,
              ),
            )
          }
        >
          <Option value="Left">Left</Option>
          <Option value="Right">Right</Option>
        </Select>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => {
        if (record.isNew) {
          return (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleCreateInstruction(record)}
            >
              Create
            </Button>
          );
        } else {
          return (
            <div className="flex gap-2">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEditInstruction(record)}
              >
                Edit
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteInstruction(record)}
              >
                Delete
              </Button>
            </div>
          );
        }
      },
    },
  ];

  return (
    <div id="dashboard">
      <Sidebar
        active="scanning_sessions"
        sidebarType="TomatoGrower"
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />
      {contextHolder}
      <div
        className={`flex flex-col w-full px-20 ${sidebarOpen ? "pl-60" : ""} pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}
      >
        <Dropdowns
          siteMetadata={siteMetadata}
          setSiteMetadata={setSiteMetadata}
          currentMetric={currentMetric}
          setCurrentMetric={setCurrentMetric}
          setMetricType={setMetricType}
          metricType={metricType}
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          currentGreenhouse={currentGreenhouse}
          setCurrentGreenhouse={setCurrentGreenhouse}
          greenhouseData={greenhouseData}
          setGreenhouseData={setGreenhouseData}
          plotData={plotData}
          setPlotData={setPlotData}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          setMetricData={setMetricData}
          metricData={metricData}
          setDropdownLoading={setDropdownLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
          setDateData={setDateData}
          dateData={dateData}
          disabledDate={disabledDate}
        />

        {loading ? (
          <LoadingIcon height="200px" classname="mt-6" />
        ) : (
          <>
            {isPastDate && (
              <>
                <div className="flex flex-row sm:flex-col gap-4 w-full my-6">
                  <Card
                    variant="outlined"
                    className="w-full flex-1 min-w-0 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
                  >
                    <Statistic
                      title="Total Scanned Sessions"
                      value={scanningSessions?.length}
                      styles={{ content: { color: getScannedFulfilledColor() } }}
                    />
                  </Card>
                  <Card
                    variant="outlined"
                    className="w-full flex-1 min-w-0 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
                  >
                    <Statistic
                      title="Total Instructed Tasks"
                      value={instructions?.length}
                    />
                  </Card>
                  <Card
                    variant="outlined"
                    className="w-full flex-1 min-w-0 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
                  >
                    <Statistic
                      title="Fulfilled Tasks"
                      value={fulfilledTasks?.length}
                      styles={{ content: { color: getFulfilledColor() } }}
                    />
                  </Card>
                </div>

                <Table
                  className="mt-4 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
                  columns={columns}
                  loading={{
                    spinning: tableLoading,
                    indicator: <LoadingIcon height="100px" />,
                  }}
                  dataSource={tableData}
                  pagination={tableData.length > 10 ? { pageSize: 10 } : false}
                  rowKey={(record) =>
                    `${record.growing_block}-${record.bay}-${record.row}-${record.side}`
                  }
                />
              </>
            )}

            {!isPastDate && currentDate && (
              <div className="mt-4">
                <Table
                  columns={creationColumns}
                  loading={{
                    spinning: tableLoading,
                    indicator: <LoadingIcon height="100px" />,
                  }}
                  dataSource={instructionRows}
                  pagination={false}
                  rowKey="key"
                />
              </div>
            )}
          </>
        )}
      </div>
      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default ScanningSessions;
